import { useLogin, useSelectProfile, useSwitchProfile } from '@/api/auth.api';
import { LoginFormData } from '@/types/auth/LoginFormData';
import { AuthProfile, SignInResponse } from '@/types/auth/AuthProfile';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { LoginState } from '@/types/auth/LoginState';
import { AuthStatus } from '@/types/auth/AuthStatus';
import { UserType } from '@/types/auth/UserType';

type UserInfo = { state: LoginState };

interface AuthResult {
  status: AuthStatus;
  userType?: UserType;
  userId?: string;
  requiresProfileSelection?: boolean;
  profiles?: AuthProfile[];
  selectionToken?: string;
  defaultUserId?: string;
  error?: string[];
}

interface IAuthContext {
  login: (loginData: LoginFormData) => Promise<AuthResult>;
  selectProfile: (
    userId: string,
    selectionToken: string,
  ) => Promise<{ status: AuthStatus; userType?: UserType }>;
  switchProfile: (
    userId: string,
  ) => Promise<{ status: AuthStatus; userType?: UserType }>;
  logout: () => void;
  userInfo?: UserInfo;
  setAccessToken: (accessToken: string) => void;
  impersonate: (impersonatedToken: string | null) => void;
  disconnectImpersonate: () => void;
  pendingProfiles: AuthProfile[];
  pendingSelectionToken: string | null;
  setPendingProfileSelection: (
    profiles: AuthProfile[],
    selectionToken: string,
  ) => void;
  clearPendingProfileSelection: () => void;
}

const AuthContext: React.Context<IAuthContext> = createContext<IAuthContext>({
  login: async () => ({ status: AuthStatus.ERROR }),
  selectProfile: async () => ({ status: AuthStatus.ERROR }),
  switchProfile: async () => ({ status: AuthStatus.ERROR }),
  logout: async () => {},
  setAccessToken: async () => {},
  impersonate: async () => {},
  disconnectImpersonate: async () => {},
  pendingProfiles: [],
  pendingSelectionToken: null,
  setPendingProfileSelection: () => {},
  clearPendingProfileSelection: () => {},
});

const PENDING_PROFILES_KEY = 'pendingProfiles';
const PENDING_SELECTION_TOKEN_KEY = 'pendingSelectionToken';

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();
  const { mutateAsync: loginApi } = useLogin();
  const { mutateAsync: selectProfileApi } = useSelectProfile();
  const { mutateAsync: switchProfileApi } = useSwitchProfile();

  const [pendingProfiles, setPendingProfiles] = useState<AuthProfile[]>(() => {
    const stored = sessionStorage.getItem(PENDING_PROFILES_KEY);
    return stored ? JSON.parse(stored) : [];
  });
  const [pendingSelectionToken, setPendingSelectionToken] = useState<
    string | null
  >(() => sessionStorage.getItem(PENDING_SELECTION_TOKEN_KEY));

  const [userInfo, setUserInfo] = useState<UserInfo>(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      try {
        const decoded = jwtDecode(accessToken);
        if (decoded.exp && decoded.exp < Date.now() / 1000 + 60) {
          localStorage.removeItem('accessToken');
          return { state: LoginState.LOGGED_OUT };
        }
        return { state: LoginState.LOGGED_IN };
      } catch {
        localStorage.removeItem('accessToken');
        return { state: LoginState.LOGGED_OUT };
      }
    }
    return { state: LoginState.LOGGED_OUT };
  });

  const clearPendingProfileSelection = useCallback(() => {
    setPendingProfiles([]);
    setPendingSelectionToken(null);
    sessionStorage.removeItem(PENDING_PROFILES_KEY);
    sessionStorage.removeItem(PENDING_SELECTION_TOKEN_KEY);
  }, []);

  const setPendingProfileSelection = useCallback(
    (profiles: AuthProfile[], selectionToken: string) => {
      setPendingProfiles(profiles);
      setPendingSelectionToken(selectionToken);
      sessionStorage.setItem(PENDING_PROFILES_KEY, JSON.stringify(profiles));
      sessionStorage.setItem(PENDING_SELECTION_TOKEN_KEY, selectionToken);
    },
    [],
  );

  const disconnect = useCallback(() => {
    setUserInfo({ state: LoginState.LOGGED_OUT });
    localStorage.removeItem('accessToken');
    axios.defaults.headers.common.Authorization = '';
    clearPendingProfileSelection();
    // Sans rechargement de page, le cache (staleTime global) servirait les
    // données de ce compte au prochain utilisateur connecté dans l'onglet.
    queryClient.clear();
  }, [clearPendingProfileSelection, queryClient]);

  const setAccessToken = useCallback(
    (accessToken: string | null) => {
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      if (!accessToken) {
        disconnect();
        return;
      }
      const decoded = jwtDecode(accessToken);
      if (decoded.exp && decoded.exp < Date.now() / 1000 + 60) {
        disconnect();
        return;
      }
      setUserInfo({ state: LoginState.LOGGED_IN });
      localStorage.setItem('accessToken', accessToken);
      clearPendingProfileSelection();
    },
    [disconnect, clearPendingProfileSelection],
  );

  const impersonate = useCallback(
    (impersonatedToken: string | null) => {
      const adminAccessToken = localStorage.getItem('accessToken');
      if (!adminAccessToken || !impersonatedToken) return;
      sessionStorage.setItem('accessToken', adminAccessToken);
      setAccessToken(impersonatedToken);
      window.location.reload();
    },
    [setAccessToken],
  );

  const disconnectImpersonate = useCallback(() => {
    const adminToken = sessionStorage.getItem('accessToken');
    if (!adminToken) disconnect();
    else {
      setAccessToken(adminToken);
      sessionStorage.removeItem('accessToken');
      window.location.reload();
    }
  }, [disconnect, setAccessToken]);

  const selectProfile = useCallback(
    async (userId: string, selectionToken: string) => {
      try {
        const { data } = await selectProfileApi({ userId, selectionToken });
        if (!data?.accessToken) {
          return { status: AuthStatus.ERROR };
        }
        setAccessToken(data.accessToken);
        return { status: AuthStatus.OK, userType: data.type };
      } catch {
        return { status: AuthStatus.ERROR };
      }
    },
    [selectProfileApi, setAccessToken],
  );

  const switchProfile = useCallback(
    async (userId: string) => {
      try {
        const { data } = await switchProfileApi(userId);
        if (!data?.accessToken) {
          return { status: AuthStatus.ERROR };
        }
        setAccessToken(data.accessToken);
        return { status: AuthStatus.OK, userType: data.type };
      } catch {
        return { status: AuthStatus.ERROR };
      }
    },
    [switchProfileApi, setAccessToken],
  );

  const login = useCallback(
    async (loginData: LoginFormData): Promise<AuthResult> => {
      try {
        const { data } = await loginApi(loginData);
        const signInData = data as SignInResponse;

        if (!signInData) {
          return { status: AuthStatus.ERROR };
        }

        if (!signInData.requiresProfileSelection) {
          setAccessToken(signInData.accessToken);
          return {
            status: AuthStatus.OK,
            userType: signInData.type,
            userId: signInData.userId,
            requiresProfileSelection: false,
          };
        }

        setPendingProfileSelection(
          signInData.profiles,
          signInData.selectionToken,
        );

        return {
          status: AuthStatus.OK,
          requiresProfileSelection: true,
          profiles: signInData.profiles,
          selectionToken: signInData.selectionToken,
          defaultUserId: signInData.defaultUserId,
        };
      } catch {
        return { status: AuthStatus.ERROR };
      }
    },
    [loginApi, setAccessToken, setPendingProfileSelection],
  );

  return (
    <AuthContext.Provider
      value={{
        login,
        selectProfile,
        switchProfile,
        logout: disconnect,
        userInfo,
        setAccessToken,
        impersonate,
        disconnectImpersonate,
        pendingProfiles,
        pendingSelectionToken,
        setPendingProfileSelection,
        clearPendingProfileSelection,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// oxlint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext<IAuthContext>(AuthContext);
